import React from 'react';
import { Button } from './ui/button';
import { ArrowRight, Star } from 'lucide-react';
import PromoHeroCard from './PromoHeroCard';

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const HeroSection = ({ onGetStarted }) => {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Image with Overlay */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1461896836934-ffe607ba8211?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxhdGhsZXRlJTIwdHJhaW5pbmd8ZW58MHx8fHwxNzgwMjMzMTUwfDA&ixlib=rb-4.1.0&q=85)',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/85 to-gray-900/70"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-6 py-28 md:py-32">
        <div className="grid lg:grid-cols-[minmax(0,1fr)_400px] gap-12 lg:gap-14 items-center">
        <div className="max-w-3xl">
          <div className="inline-block mb-6 px-4 py-2 bg-yellow-600/20 border border-yellow-600/30 rounded-full">
            <span className="text-yellow-400 font-semibold text-xs sm:text-sm tracking-wider">
              OKLEVELES SPORTEDZŐ · MISKOLC, BB GYM
            </span>
          </div>

          <h1
            data-testid="hero-heading"
            className="mb-6"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            <span className="block text-3xl sm:text-4xl lg:text-5xl font-bold text-yellow-400 leading-tight">
              Fáradtnak és merevnek érzed a tested munka közben vagy után?
            </span>
            <span className="block mt-4 text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight">
              Fogyj, erősödj és legyél energikusabb – koplalás és extrém terhelés nélkül.
            </span>
          </h1>

          <p className="text-base md:text-lg text-gray-300 mb-8 leading-relaxed max-w-2xl">
            Nem sablon edzéstervvel: egy <span className="text-white font-semibold">díjmentes konzultáción</span> átbeszéljük,
            mi a célod, mennyi időd van rá, és erre építjük fel a saját tervedet.
            14 év tapasztalattal és tudományos alapokon segítek, hogy kezdőként is sikereket érj el!
          </p>

          {/* CTAs */}
          <div className="max-w-xl">
            <Button
              data-testid="hero-get-started-btn"
              onClick={onGetStarted}
              size="lg"
              className="w-full bg-yellow-600 hover:bg-yellow-500 text-gray-900 font-bold text-base md:text-lg px-8 py-6 h-auto whitespace-normal leading-snug transition-all duration-300 hover:scale-[1.02] shadow-lg shadow-yellow-900/30"
            >
              Ingyenes konzultációt kérek
              <ArrowRight className="ml-2 shrink-0" size={20} />
            </Button>
            <div data-testid="hero-cta-subtext" className="mt-3 text-xs sm:text-sm text-gray-400 text-center">
              20 perc · átbeszéljük a célod · semmire nem kötelez
              <span className="block mt-1">Személyesen a BB Gymben vagy online – ahogy neked kényelmes.</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
              <Button
                data-testid="hero-how-i-work-btn"
                variant="outline"
                size="lg"
                onClick={() => scrollTo('how-we-work')}
                className="border-2 border-white/70 bg-transparent text-white hover:bg-white hover:text-gray-900 font-bold py-5 h-auto whitespace-normal leading-snug transition-all duration-300"
              >
                Így dolgozom
              </Button>
              <Button
                data-testid="hero-view-programs-btn"
                variant="outline"
                size="lg"
                onClick={() => scrollTo('programs')}
                className="border-2 border-white/70 bg-transparent text-white hover:bg-white hover:text-gray-900 font-bold py-5 h-auto whitespace-normal leading-snug transition-all duration-300"
              >
                Árak, programok
              </Button>
            </div>

            {/* Social proof */}
            <div
              data-testid="hero-review-row"
              className="mt-8 pt-6 border-t border-gray-700/80 grid grid-cols-1 sm:grid-cols-2 gap-5"
            >
              <div data-testid="hero-review-miklos">
                <div className="flex items-center gap-1 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="text-yellow-500 fill-yellow-500" size={15} />
                  ))}
                </div>
                <p className="text-sm text-gray-300 leading-relaxed break-words">
                  „Két és fél hónap alatt 11 kilót sikerült leadnom, és sokkal fittebbnek érzem magam.”
                  <span className="text-gray-500"> – Miklós</span>
                </p>
              </div>
              <div data-testid="hero-review-patricia">
                <div className="flex items-center gap-1 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="text-yellow-500 fill-yellow-500" size={15} />
                  ))}
                </div>
                <p className="text-sm text-gray-300 leading-relaxed break-words">
                  „Kedves, türelmes, értelmesen magyaráz.”
                  <span className="text-gray-500"> – Patricia</span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Aktuális ajánlat — a hero üres része */}
        <div className="lg:justify-self-end w-full flex lg:block justify-center">
          <PromoHeroCard onBooking={onGetStarted} testId="promo-hero-card" />
        </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
